import React from 'react';
import { Link } from 'react-router-dom';

const planCards = [
  {
    name: 'Starter',
    subtitle: 'For early-stage and independent providers',
    monthly: '£5',
    summary:
      'A lightweight entry plan for providers who want listing visibility, booking-readiness, and the essentials needed to begin operating on HnB.',
    features: [
      'Public provider profile',
      'Service catalogue listing',
      'Booking calendar access',
      'Automated reminders',
      'Basic visibility across HnB discovery surfaces',
    ],
  },
  {
    name: 'Growth',
    subtitle: 'For active local salons and studios',
    monthly: '£29',
    summary:
      'A stronger operating plan for providers who want better discovery, clearer reporting, and more tools to support consistent customer flow.',
    features: [
      'Everything in Starter',
      'Featured placement opportunities',
      'Basic analytics and reporting',
      'Marketing tools and promotional support',
      'Stronger commercial visibility across the platform',
    ],
  },
  {
    name: 'Pro',
    subtitle: 'For higher-volume providers',
    monthly: '£59',
    summary:
      'A more advanced tier for businesses that want deeper analytics, stronger marketing support, and a more developed operating setup inside HnB.',
    features: [
      'Everything in Growth',
      'Advanced analytics',
      'Deeper campaign and marketing tools',
      'Priority support direction',
      'Earlier access to future premium capabilities',
    ],
  },
];

const pricingRows = [
  ['Starter', 'Monthly', '£5'],
  ['Starter', 'Quarterly', '£12'],
  ['Starter', 'Half-yearly', '£22'],
  ['Starter', 'Annual', '£42'],
  ['Growth', 'Monthly', '£29'],
  ['Growth', 'Quarterly', '£80'],
  ['Growth', 'Half-yearly', '£159'],
  ['Growth', 'Annual', '£319'],
  ['Pro', 'Monthly', '£59'],
  ['Pro', 'Quarterly', '£170'],
  ['Pro', 'Half-yearly', '£339'],
  ['Pro', 'Annual', '£675'],
];

const benefits = [
  {
    title: 'Visibility and discovery',
    body: 'HnB is designed to help independent providers become easier to find, compare, and trust across local personal service categories.',
  },
  {
    title: 'Portfolio-led marketing',
    body: 'Providers can showcase real work through structured categories, stylist visibility, and future work-sample presentation.',
  },
  {
    title: 'Booking and operations',
    body: 'The wider platform direction includes bookings, deposits, confirmations, reporting, and stronger operational transparency.',
  },
  {
    title: 'Scalable growth',
    body: 'Provider plans are intended to sit inside a broader ERP-ready model rather than act as a simple directory fee.',
  },
];

const governanceNotes = [
  'Providers go through staged onboarding, verification, service setup, calendar configuration, and go-live approval.',
  'Pricing changes should apply from the next billing cycle and respect the current active plan.',
  'Owner and stylist permissions remain distinct, especially for subscriptions, refunds, and wider finance controls.',
  'Commission and subscription structures should remain transparent and adjustable through authorised HnB administration later.',
];

export default function ProviderPlansPage() {
  return (
    <main style={{ background: '#f8fafc', color: '#0f172a', minHeight: '100vh' }}>
      <section
        style={{
          padding: '72px 24px 56px',
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            maxWidth: '1120px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '28px',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.9rem',
              }}
            >
              Provider plans
            </p>

            <h1
              style={{
                margin: 0,
                fontSize: '3rem',
                lineHeight: 1.08,
                maxWidth: '12ch',
              }}
            >
              Clear commercial options for providers joining HnB
            </h1>

            <p
              style={{
                margin: 0,
                color: '#475569',
                lineHeight: 1.8,
                fontSize: '1.05rem',
                maxWidth: '72ch',
              }}
            >
              HnB is being built not only for customers, but also for salons,
              barbers, nail studios, tattoo shops, piercing providers, and other
              service businesses that need stronger visibility, better workflow
              support, and room to grow on a structured local platform.
            </p>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Link
                to="/contact"
                style={{
                  textDecoration: 'none',
                  background: '#111827',
                  color: '#ffffff',
                  padding: '13px 18px',
                  borderRadius: '10px',
                  fontWeight: 600,
                }}
              >
                Talk to HnB
              </Link>

              <Link
                to="/find-provider"
                style={{
                  textDecoration: 'none',
                  background: '#ffffff',
                  color: '#111827',
                  padding: '13px 18px',
                  borderRadius: '10px',
                  fontWeight: 600,
                  border: '1px solid #d1d5db',
                }}
              >
                Explore providers
              </Link>
            </div>
          </div>

          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '22px',
              padding: '24px',
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            <div
              style={{
                alignSelf: 'flex-start',
                padding: '8px 12px',
                borderRadius: '999px',
                background: '#eef2ff',
                color: '#4338ca',
                fontWeight: 700,
                fontSize: '0.88rem',
              }}
            >
              Provider proposition
            </div>

            <h2 style={{ margin: 0, fontSize: '1.4rem' }}>
              More than a directory listing
            </h2>

            <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.75 }}>
              The HnB provider offer combines visibility, discovery, portfolio-led
              marketing, booking-readiness, and future reporting rather than acting
              as a basic listing-only subscription.
            </p>

            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
                borderRadius: '16px',
                padding: '16px',
              }}
            >
              <p style={{ margin: 0, color: '#334155', lineHeight: 1.7 }}>
                Subscription pricing and commercial rules can evolve over time, but
                the structure should remain clear, transparent, and provider-friendly.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 24px' }}>
        <div
          style={{
            maxWidth: '1120px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '22px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.85rem',
              }}
            >
              Why join
            </p>
            <h2 style={{ margin: 0, fontSize: '2rem' }}>
              What providers should gain from HnB
            </h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '16px',
            }}
          >
            {benefits.map((item) => (
              <div
                key={item.title}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '18px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.08rem' }}>{item.title}</h3>
                <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.75 }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: '56px 24px',
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            maxWidth: '1120px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '22px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.85rem',
              }}
            >
              Plan overview
            </p>
            <h2 style={{ margin: 0, fontSize: '2rem' }}>
              Subscription tiers for different provider stages
            </h2>
            <p style={{ margin: 0, color: '#64748b', lineHeight: 1.75, maxWidth: '72ch' }}>
              The current commercial model supports three provider tiers, each with
              clearer value as businesses grow in booking volume, visibility needs,
              and reporting requirements.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '18px',
            }}
          >
            {planCards.map((plan) => (
              <div
                key={plan.name}
                style={{
                  background: '#ffffff',
                  border: plan.name === 'Growth' ? '1px solid #818cf8' : '1px solid #e5e7eb',
                  borderRadius: '20px',
                  padding: '22px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  boxShadow:
                    plan.name === 'Growth'
                      ? '0 12px 32px rgba(79, 70, 229, 0.10)'
                      : 'none',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: '1.15rem' }}>{plan.name}</h3>
                    {plan.name === 'Growth' && (
                      <span
                        style={{
                          padding: '7px 10px',
                          borderRadius: '999px',
                          background: '#eef2ff',
                          color: '#4338ca',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                        }}
                      >
                        Most balanced
                      </span>
                    )}
                  </div>

                  <p style={{ margin: 0, color: '#4338ca', fontWeight: 600 }}>
                    {plan.subtitle}
                  </p>
                </div>

                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '2rem',
                      fontWeight: 700,
                      color: '#111827',
                    }}
                  >
                    {plan.monthly}
                    <span style={{ fontSize: '1rem', fontWeight: 500, color: '#6b7280' }}>
                      {' '}
                      / month
                    </span>
                  </p>
                </div>

                <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.75 }}>
                  {plan.summary}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {plan.features.map((feature) => (
                    <div
                      key={feature}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          width: '10px',
                          height: '10px',
                          marginTop: '7px',
                          borderRadius: '999px',
                          background: '#4f46e5',
                          flexShrink: 0,
                        }}
                      />
                      <p style={{ margin: 0, color: '#334155', lineHeight: 1.65 }}>
                        {feature}
                      </p>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  style={{
                    marginTop: '4px',
                    textAlign: 'center',
                    textDecoration: 'none',
                    background: plan.name === 'Growth' ? '#111827' : '#ffffff',
                    color: plan.name === 'Growth' ? '#ffffff' : '#111827',
                    border: plan.name === 'Growth' ? 'none' : '1px solid #d1d5db',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    fontWeight: 600,
                  }}
                >
                  Enquire about {plan.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '24px 24px 56px' }}>
        <div
          style={{
            maxWidth: '1120px',
            margin: '0 auto',
            overflowX: 'auto',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '20px',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={thStyle}>Plan</th>
                <th style={thStyle}>Billing period</th>
                <th style={thStyle}>Total price</th>
              </tr>
            </thead>
            <tbody>
              {pricingRows.map((row) => (
                <tr key={row.join('-')}>
                  {row.map((cell, index) => (
                    <td key={`${row[0]}-${row[1]}-${index}`} style={tdStyle}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ padding: '0 24px 56px' }}>
        <div
          style={{
            maxWidth: '1120px',
            margin: '0 auto',
            background: '#eef2ff',
            color: '#312e81',
            border: '1px solid #c7d2fe',
            borderRadius: '16px',
            padding: '18px 20px',
          }}
        >
          <p style={{ margin: 0, lineHeight: 1.7 }}>
            HnB’s wider model can also support commission-based charging logic, but
            these subscription tiers provide the clearest public starting point for
            provider onboarding and platform participation.
          </p>
        </div>
      </section>

      <section style={{ padding: '56px 24px' }}>
        <div
          style={{
            maxWidth: '1120px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 360px) 1fr',
            gap: '24px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.85rem',
              }}
            >
              Governance and pricing notes
            </p>
            <h2 style={{ margin: 0, fontSize: '2rem', lineHeight: 1.15 }}>
              Provider terms should stay clear and fair
            </h2>
            <p style={{ margin: 0, color: '#64748b', lineHeight: 1.75 }}>
              HnB’s business rules repeatedly emphasise transparency, role clarity,
              and future commercial discipline across provider operations.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '12px',
            }}
          >
            {governanceNotes.map((note) => (
              <div
                key={note}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '16px',
                  padding: '16px 18px',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    marginTop: '7px',
                    borderRadius: '999px',
                    background: '#4f46e5',
                    flexShrink: 0,
                  }}
                />
                <p style={{ margin: 0, color: '#334155', lineHeight: 1.7 }}>{note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: '64px 24px 72px',
          background: '#0f172a',
          color: '#ffffff',
        }}
      >
        <div
          style={{
            maxWidth: '960px',
            margin: '0 auto',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            alignItems: 'center',
          }}
        >
          <p
            style={{
              margin: 0,
              color: '#a5b4fc',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontSize: '0.85rem',
            }}
          >
            Provider onboarding
          </p>

          <h2 style={{ margin: 0, fontSize: '2.2rem', lineHeight: 1.15 }}>
            Start the conversation about joining HnB
          </h2>

          <p
            style={{
              margin: 0,
              color: '#cbd5e1',
              lineHeight: 1.8,
              maxWidth: '62ch',
            }}
          >
            Use this page to introduce the provider offer publicly, then move
            interested businesses into a more detailed onboarding or contact flow.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Link
              to="/contact"
              style={{
                textDecoration: 'none',
                background: '#ffffff',
                color: '#111827',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
              }}
            >
              Contact HnB
            </Link>

            <Link
              to="/about"
              style={{
                textDecoration: 'none',
                background: 'transparent',
                color: '#ffffff',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              About HnB
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '14px 16px',
  borderBottom: '1px solid #e5e7eb',
  color: '#111827',
  fontSize: '0.95rem',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  borderBottom: '1px solid #f1f5f9',
  color: '#475569',
  lineHeight: 1.6,
};